import { useMemo, useState } from 'react';
import {
  collectGroupMemberIds,
  normalizeNodeGroups,
  staleNodeIdsForGroup,
} from '../../lib/nodeGroups';
import { useEditorStore } from '../../store/editorStore';
import type { NodeGroup } from '../../types/project';

// ------------------------------------------------------------
// Node groups — view and edit project groups
// ------------------------------------------------------------

export default function NodeGroupsPanel() {
  const document = useEditorStore((s) => s.document);
  const readOnly = useEditorStore((s) => s.permission) !== 'edit';
  const setNodeSelection = useEditorStore((s) => s.setNodeSelection);
  const removeStaleNodeGroupReference = useEditorStore(
    (s) => s.removeStaleNodeGroupReference,
  );
  const applyNodeGroups = useEditorStore((s) => s.applyNodeGroups);
  const createNodeGroup = useEditorStore((s) => s.createNodeGroup);
  const deleteNodeGroup = useEditorStore((s) => s.deleteNodeGroup);
  const [expandedGroupId, setExpandedGroupId] = useState<string | null>(null);
  const [newGroupName, setNewGroupName] = useState('');
  const [renameDraft, setRenameDraft] = useState<Record<string, string>>({});

  const nodes = document?.nodes ?? [];
  const groups = useMemo(
    () => (document ? normalizeNodeGroups(document) : []),
    [document],
  );

  const nodesById = useMemo(
    () => new Map(nodes.map((node) => [node.id, node])),
    [nodes],
  );

  if (!document) {
    return (
      <p className="px-4 py-6 text-center text-xs text-text-subtle">
        No project loaded
      </p>
    );
  }

  const updateGroup = (groupId: string, updater: (group: NodeGroup) => NodeGroup) => {
    const next = groups.map((group) =>
      group.id === groupId ? updater(group) : group,
    );
    applyNodeGroups(next);
  };

  const toggleNodeInGroup = (groupId: string, nodeId: string) => {
    updateGroup(groupId, (group) => {
      const memberIds = collectGroupMemberIds(group, nodes);
      if (memberIds.has(nodeId)) memberIds.delete(nodeId);
      else memberIds.add(nodeId);
      return { ...group, node_ids: [...memberIds] };
    });
  };

  const handleCreateGroup = () => {
    const name = newGroupName.trim();
    if (!name) return;
    createNodeGroup(name);
    setNewGroupName('');
    setExpandedGroupId(name);
  };

  const handleRename = (groupId: string) => {
    const draft = renameDraft[groupId]?.trim();
    if (!draft) return;
    updateGroup(groupId, (group) => ({
      ...group,
      id: group.id,
      name: draft,
    }));
    setRenameDraft((prev) => {
      const next = { ...prev };
      delete next[groupId];
      return next;
    });
  };

  return (
    <div className="space-y-3 p-3">
      <p className="text-[11px] leading-relaxed text-text-subtle">
        Groups organize nodes for simulation and the Elements list. Assign nodes
        here or via each node&apos;s Group field.
      </p>

      {!readOnly && (
        <div className="flex gap-2">
          <input
            type="text"
            value={newGroupName}
            onChange={(e) => setNewGroupName(e.target.value)}
            placeholder="New group name…"
            className="input-field min-w-0 flex-1 py-1.5 text-xs"
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleCreateGroup();
            }}
          />
          <button
            type="button"
            className="btn-secondary shrink-0 px-3 py-1.5 text-xs"
            onClick={handleCreateGroup}
            disabled={!newGroupName.trim()}
          >
            Add
          </button>
        </div>
      )}

      {groups.length === 0 ? (
        <p className="rounded-lg border border-dashed border-border px-4 py-6 text-center text-xs text-text-subtle">
          No groups defined yet.
        </p>
      ) : (
        <ul className="space-y-2">
          {groups.map((group) => {
            const memberIds = collectGroupMemberIds(group, nodes);
            const members = [...memberIds]
              .map((id) => nodesById.get(id))
              .filter((node): node is NonNullable<typeof node> => node != null)
              .sort((a, b) => a.name.localeCompare(b.name));
            const staleIds = staleNodeIdsForGroup(group, nodes);
            const expanded = expandedGroupId === group.id;
            const renameValue = renameDraft[group.id] ?? group.name;

            return (
              <li
                key={group.id}
                className="rounded-lg border border-border bg-surface/40"
              >
                <button
                  type="button"
                  className="flex w-full items-start gap-2 px-3 py-2 text-left"
                  onClick={() =>
                    setExpandedGroupId(expanded ? null : group.id)
                  }
                >
                  <span className="mt-0.5 text-text-subtle">
                    {expanded ? '▾' : '▸'}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium text-text">
                      {group.name}
                    </span>
                    <span className="mt-0.5 block text-[10px] text-text-subtle">
                      {members.length} node{members.length === 1 ? '' : 's'}
                      {staleIds.length > 0
                        ? ` · ${staleIds.length} stale reference${
                            staleIds.length === 1 ? '' : 's'
                          }`
                        : ''}
                    </span>
                  </span>
                  {members.length > 0 && (
                    <button
                      type="button"
                      className="btn-ghost shrink-0 px-2 py-1 text-[10px]"
                      onClick={(e) => {
                        e.stopPropagation();
                        setNodeSelection(members.map((node) => node.id), {
                          focus: true,
                        });
                      }}
                    >
                      Show
                    </button>
                  )}
                </button>

                {expanded && (
                  <div className="space-y-3 border-t border-border px-3 py-3">
                    {!readOnly && (
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={renameValue}
                          onChange={(e) =>
                            setRenameDraft((prev) => ({
                              ...prev,
                              [group.id]: e.target.value,
                            }))
                          }
                          className="input-field min-w-0 flex-1 py-1.5 text-xs"
                        />
                        <button
                          type="button"
                          className="btn-ghost shrink-0 px-2 py-1 text-[10px]"
                          onClick={() => handleRename(group.id)}
                        >
                          Rename
                        </button>
                        <button
                          type="button"
                          className="btn-danger-ghost shrink-0 px-2 py-1 text-[10px]"
                          onClick={() => {
                            deleteNodeGroup(group.id);
                            if (expandedGroupId === group.id) {
                              setExpandedGroupId(null);
                            }
                          }}
                        >
                          Delete
                        </button>
                      </div>
                    )}

                    {staleIds.length > 0 && (
                      <div className="rounded-md border border-warning/35 bg-warning-muted/15 px-3 py-2 text-xs text-warning-fg">
                        <p className="font-medium">Stale references</p>
                        <ul className="mt-1 space-y-1">
                          {staleIds.map((staleId) => (
                            <li
                              key={staleId}
                              className="flex items-center justify-between gap-2"
                            >
                              <span className="truncate font-mono text-[10px]">
                                {staleId}
                              </span>
                              {!readOnly && (
                                <button
                                  type="button"
                                  className="btn-ghost shrink-0 px-2 py-0.5 text-[10px]"
                                  onClick={() =>
                                    removeStaleNodeGroupReference(staleId)
                                  }
                                >
                                  Remove
                                </button>
                              )}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    <div>
                      <p className="mb-1 text-[10px] font-medium uppercase tracking-wide text-text-subtle">
                        Members
                      </p>
                      {members.length === 0 ? (
                        <p className="text-xs text-text-subtle">
                          No nodes in this group yet.
                        </p>
                      ) : (
                        <ul className="max-h-32 space-y-0.5 overflow-y-auto">
                          {members.map((node) => (
                            <li
                              key={node.id}
                              className="flex items-center justify-between gap-2 rounded px-1 py-0.5 text-xs text-text-muted"
                            >
                              <span className="truncate">{node.name}</span>
                              {!readOnly && (
                                <button
                                  type="button"
                                  className="btn-ghost px-2 py-0.5 text-[10px]"
                                  onClick={() =>
                                    toggleNodeInGroup(group.id, node.id)
                                  }
                                >
                                  Remove
                                </button>
                              )}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>

                    {!readOnly && (
                      <div>
                        <p className="mb-1 text-[10px] font-medium uppercase tracking-wide text-text-subtle">
                          Add nodes
                        </p>
                        <ul className="max-h-40 space-y-0.5 overflow-y-auto">
                          {nodes
                            .filter((node) => !memberIds.has(node.id))
                            .sort((a, b) => a.name.localeCompare(b.name))
                            .map((node) => (
                              <li key={node.id}>
                                <button
                                  type="button"
                                  className="flex w-full items-center justify-between rounded px-1 py-0.5 text-left text-xs text-text-muted hover:bg-surface-overlay hover:text-text"
                                  onClick={() =>
                                    toggleNodeInGroup(group.id, node.id)
                                  }
                                >
                                  <span className="truncate">{node.name}</span>
                                  <span className="shrink-0 text-[10px] text-text-subtle">
                                    Add
                                  </span>
                                </button>
                              </li>
                            ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
